import React from "react";
import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import {
  ChartPieSlice,
  Buildings,
  Compass,
  Scissors,
  TrendUp,
  Vault,
} from "@phosphor-icons/react";

const SERVIZI = [
  {
    id: "01",
    Icon: ChartPieSlice,
    title: "Controllo di Gestione",
    body: "Identifichiamo le business unit, misuriamo i margini reali e costruiamo un cruscotto che ti permette di decidere con dati, non con l'istinto.",
    tag: "Cruscotto & KPI",
  },
  {
    id: "02",
    Icon: Compass,
    title: "Strategia & Piano Industriale",
    body: "Quando i numeri sono chiari, si può costruire una direzione. Ti aiutiamo a definire una strategia di crescita concreta: sviluppo interno, acquisizioni, potenziamento delle aree più redditizie ed eliminazione di quelle che erodono i margini. Un piano industriale non è un documento — è la bussola dell'azienda.",
    tag: "Direzione & Crescita",
  },
  {
    id: "03",
    Icon: Scissors,
    title: "Ottimizzazione dei Costi",
    body: "Tagliare non basta. Ottimizzare significa liberare risorse da reinvestire nella crescita. Fino al 10-15% dei costi operativi è recuperabile.",
    tag: "Efficienza operativa",
  },
  {
    id: "04",
    Icon: Buildings,
    title: "Struttura Societaria",
    body: "La forma societaria giusta è un vantaggio competitivo. Ti aiutiamo a strutturare l'azienda come veicolo di ottimizzazione fiscale e patrimoniale.",
    tag: "Governance & Patrimonio",
  },
  {
    id: "05",
    Icon: TrendUp,
    title: "Finanza Alternativa",
    body: "Oltre il credito bancario: private equity, minibond, crowdfunding, agevolazioni. Ti aiutiamo a trovare le risorse giuste per crescere.",
    tag: "Growth Capital",
  },
  {
    id: "06",
    Icon: Vault,
    title: "Gestione Patrimoniale",
    body: "Fai lavorare i tuoi soldi! Noi offriamo una gestione indipendente — azioni, obbligazioni, ETF — senza le commissioni tipiche delle banche e dei loro prodotti, e senza conflitti di interesse.",
    tag: "Asset & Wealth",
  },
];

export default function Servizi() {
  return (
    <section
      id="servizi"
      data-testid="section-servizi"
      className="relative py-32 md:py-40 lg:py-48 overflow-hidden bg-navy-deep"
    >
      {/* Diagonal thin lines pattern */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="diag" width="24" height="24" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="24" stroke="#c9a84c" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#diag)" />
        </svg>
      </div>

      <div
        aria-hidden
        className="absolute top-16 right-6 md:right-10 font-display italic text-[14rem] md:text-[22rem] leading-none text-gold/[0.05] select-none pointer-events-none"
      >
        04
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="max-w-4xl mb-20 md:mb-24">
          <Reveal>
            <div className="flex items-center gap-4 mb-8">
              <span className="w-10 h-px bg-gold" />
              <span
                className="text-[0.7rem] uppercase text-gold font-sans-crisp font-semibold"
                style={{ letterSpacing: "0.3em" }}
              >
                Capitolo 04 · Servizi
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.02}>
            <p
              className="text-[0.72rem] uppercase text-ink/50 font-sans-crisp font-medium mb-4"
              style={{ letterSpacing: "0.32em" }}
              data-testid="servizi-kicker"
            >
              Di Cosa Ci Occupiamo
            </p>
          </Reveal>

          <Reveal delay={0.05}>
            <h2
              data-testid="servizi-title"
              className="font-display font-medium tracking-tight leading-[1.02] text-4xl md:text-5xl lg:text-6xl text-ink"
            >
              Soluzioni <span className="italic-serif text-gold">su misura</span>,
              <br className="hidden md:block" /> non prodotti standard.
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <p
              className="mt-8 text-lg md:text-xl text-ink/70 leading-relaxed max-w-2xl font-sans-crisp"
              data-testid="servizi-subtitle"
            >
              Ogni azienda è diversa. Il Pacchetto Diagnosi è il punto di partenza —
              da lì, ogni intervento è costruito su misura, come un abito sartoriale.
            </p>
          </Reveal>
        </div>

        {/* Bento 3 + 3 layout */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6 md:gap-8">
          {SERVIZI.map((s, i) => {
            // 6 cards on desktop: two rows of 3 (each card = 2 of 6 cols)
            const desktopSpan = "md:col-span-2";
            return (
              <motion.article
                key={s.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.08 }}
                className={`group relative border border-gold/25 hover:border-gold/70 bg-navy-mid/25 hover:bg-navy-mid/50 backdrop-blur-sm p-8 md:p-10 lg:p-12 transition-colors duration-700 ${desktopSpan}`}
                data-testid={`servizio-card-${s.id}`}
              >
                {/* Top row: numeral + icon */}
                <div className="flex items-start justify-between mb-8">
                  <span
                    className="font-display italic text-gold text-sm"
                    style={{ letterSpacing: "0.1em" }}
                  >
                    {s.id} / 06
                  </span>
                  <div className="w-14 h-14 border border-gold/40 group-hover:border-gold group-hover:rotate-6 flex items-center justify-center transition-all duration-700">
                    <s.Icon size={26} weight="light" className="text-gold" />
                  </div>
                </div>

                <h3 className="font-display text-2xl md:text-3xl lg:text-4xl text-ink leading-tight mb-5">
                  {s.title}
                </h3>
                <p className="text-ink/70 leading-relaxed text-base md:text-lg mb-8 font-sans-crisp">
                  {s.body}
                </p>

                <div className="pt-6 border-t border-gold/15">
                  <span
                    className="text-[0.7rem] uppercase text-gold/85 font-sans-crisp font-medium"
                    style={{ letterSpacing: "0.24em" }}
                  >
                    {s.tag}
                  </span>
                </div>

                {/* Hover corner accent */}
                <span className="absolute top-3 left-3 w-3 h-px bg-gold/0 group-hover:bg-gold transition-colors duration-700" />
                <span className="absolute top-3 left-3 h-3 w-px bg-gold/0 group-hover:bg-gold transition-colors duration-700" />
                <span className="absolute bottom-3 right-3 w-3 h-px bg-gold/0 group-hover:bg-gold transition-colors duration-700" />
                <span className="absolute bottom-3 right-3 h-3 w-px bg-gold/0 group-hover:bg-gold transition-colors duration-700" />
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
